import React, { useState, useEffect } from "react";
import classnames from "classnames";
import _, { set } from "lodash";
import { Part, QualityAlert } from "../../../models/quality-alert";
import CustomModal from "./discrepantMaterialModal";
import {
  QualityAlertDiscrepantMaterial,
  QualityAlertLocationInfo,
} from "../../../models/quality-alert";

interface Props {
  supplierId?: number;
  locationId?: number;
  qualityAlertId?: number | "new";
  height?: number;
  hideCount?: boolean;
  submitted?: boolean;
  className?: string;
  partsList?: Part[];
  onDiscrepanctMaterialChange?: (
    discrepantMaterial: QualityAlertDiscrepantMaterial,
  ) => void;
  updateQualityAlert: (qualityAlert: any) => void;
  qualityAlert: QualityAlert | null;
  qualityAlertLocationInfo: QualityAlertLocationInfo | null;
  setQualityAlertLocationInfo: (qualityAlertLocationInfo: any) => void;
}

interface TableDiscrepantMaterial {
  qty: number;
  disposition: string;
  partNumber: string;
  description: string;
}

const PartsTableContainer: React.FC<{
  data: Part[];
  search: string;
  onRowClick: (part: Part, rowIndex: number) => void;
  selectedRow: number | null;
}> = ({ data, search, onRowClick, selectedRow }) => {
  const filteredData = data.filter(
    (part) =>
      part.desc.toLowerCase().includes(search.toLowerCase()) ||
      part.number.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="h-64 overflow-auto">
      <table className="min-w-full border-collapse overflow-scroll overflow-x-auto border border-gray-200 text-black">
        <thead>
          <tr className="bg-gray-100">
            <th className="border-b border-gray-200 px-4 py-2 text-left">
              Description
            </th>
            <th className="border-b border-gray-200 px-4 py-2 text-left">
              Part No
            </th>
            <th className="border-b border-gray-200 px-4 py-2 text-left">
              Production Team
            </th>
          </tr>
        </thead>
        <tbody className="text-black">
          {filteredData.map((part, index) => (
            <tr
              key={part.id}
              className={classnames(
                "cursor-pointer hover:bg-gray-100",
                selectedRow === index ? "bg-blue-100" : "",
              )}
              onClick={() => onRowClick(part, index)}
            >
              <td className="border-b border-gray-200 px-4 py-2">
                {part.desc}
              </td>
              <td className="border-b border-gray-200 px-4 py-2">
                {part.number}
              </td>
              <td className="border-b border-gray-200 px-4 py-2">
                {part.productionTeam}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

//TODO fix onRowClick to be a Part object
const DiscrepantMaterialTableContainer: React.FC<{
  data: QualityAlertDiscrepantMaterial[];
  parts: Part[];
  onRowClick: (part: QualityAlertDiscrepantMaterial, rowIndex: number) => void;
  className?: string;
}> = ({ data, onRowClick }) => {
  return (
    <div className="overflow-auto">
      <table className="min-w-full border-collapse overflow-scroll border border-gray-200">
        <thead>
          <tr className="bg-gray-100">
            <th className="border-b border-gray-200 px-4 py-2 text-left">
              Description
            </th>
            <th className="border-b border-gray-200 px-4 py-2 text-left">
              Part No
            </th>
            <th className="border-b border-gray-200 px-4 py-2 text-left">
              Disposition
            </th>
            <th className="border-b border-gray-200 px-4 py-2 text-left">
              Qty
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr
              key={index}
              className="cursor-pointer hover:bg-gray-100"
              onClick={() => onRowClick(row, index)}
            >
              <td className="border-b border-gray-200 px-4 py-2">
                {row.partObject?.desc}
              </td>
              <td className="border-b border-gray-200 px-4 py-2">
                {row.partObject?.number}
              </td>
              <td className="border-b border-gray-200 px-4 py-2">
                {row.disposition}
              </td>
              <td className="border-b border-gray-200 px-4 py-2">{row.qty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PartsSelector: React.FC<Props> = ({
  supplierId,
  locationId,
  qualityAlertId,
  hideCount,
  submitted,
  className,
  partsList,
  updateQualityAlert,
  qualityAlert,
  qualityAlertLocationInfo,
  setQualityAlertLocationInfo,
}) => {
  const [unselectedParts, setUnselectedParts] = useState<{
    part: Part | null;
    selectedRow: number | null;
  }>({
    part: null,
    selectedRow: null,
  });
  const [selectedPart, setSelectedPart] = useState<Part>();
  const [isNewAffectedPart, setIsNewAffectedPart] = useState<boolean>(false);
  const [selectedDiscrepantMaterialIndex, setSelectedDiscrepantMaterialIndex] =
    useState<number | null>(null);
  const [selectedParts, setSelectedParts] = useState<Part>();
  const [affectedParts, setAffectedParts] = useState<
    QualityAlertDiscrepantMaterial[]
  >([]);
  const [search, setSearch] = useState<string>("");
  const [parts, setParts] = useState<Part[]>(partsList || []);
  const [discrepantMaterial, setDiscrepantMaterial] =
    useState<QualityAlertDiscrepantMaterial>(
      {} as QualityAlertDiscrepantMaterial,
    );
  const [discrepantMaterialsModalOpen, setDiscrepantMaterialsModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (partsList && partsList.length > 0) {
      setParts(partsList);
    }
  }, [partsList]);

  const validate = (): string[] => {
    const invalid: string[] = [];
    const parts = getDiscrepantMaterial();
    if (parts && parts.length === 0) {
      invalid.push("parts");
    }
    return invalid;
  };

  const getDiscrepantMaterial = (): QualityAlertDiscrepantMaterial[] => {
    let data = affectedParts || [];
    return data;
  };

  const getUnselectedParts = (): Part[] => {
    let data = parts || [];
    let dMaterial = getDiscrepantMaterial();

    data = _.filter(
      data,
      (part) => part.active && !_.find(dMaterial || [], { part: part.id }),
    );

    return _.sortBy(data, ["category", "desc", "number"]);
  };

  // TODO partTemp not used here for now ADAM
  const onRowClickDiscrepantMaterial = (
    partTemp: QualityAlertDiscrepantMaterial,
    rowIndex: number,
  ) => {
    const selectedMaterial = getDiscrepantMaterial()[rowIndex];
    setSelectedDiscrepantMaterialIndex(rowIndex);
    setDiscrepantMaterial(selectedMaterial);
    setDiscrepantMaterialsModalOpen(!discrepantMaterialsModalOpen);
  };

  const onRowClickUnselectedParts = (part: Part, rowIndex: number) => {
    setUnselectedParts((prev) => ({
      selectedRow: prev.selectedRow === rowIndex ? null : rowIndex,
      part,
    }));
    setSelectedPart(part);
  };

  const onSearch = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(evt.target.value);
  };

  const onSelectPart = () => {
    if (!selectedPart) return;
    createNewMaterial(selectedPart);
  };

  const createNewMaterial = (selectedPart: Part) => {
    setDiscrepantMaterialsModalOpen(!discrepantMaterialsModalOpen);
  };

  const onAddClick = () => {
    // open modal to create new part
    // use service to create new part
    // actionsSuppliers.createPart(supplierId)
  };

  const saveDiscrepantMaterials = (
    quantity: number,
    responsibility: string,
    disposition: string,
  ) => {
    if (!discrepantMaterial) return;
    discrepantMaterial.qty = quantity;
    discrepantMaterial.responsibility = responsibility;
    discrepantMaterial.disposition = disposition;
    discrepantMaterial.part = selectedPart?.id ?? -1;
    discrepantMaterial.partObject = selectedPart || ({} as Part);
    discrepantMaterial.supplier = supplierId || -1;
    setDiscrepantMaterial(discrepantMaterial);
    setParts(_.remove(parts, (part) => part.id !== selectedPart?.id));
    // add to affected parts table
    if (selectedDiscrepantMaterialIndex === null) {
      affectedParts.push({ ...discrepantMaterial });
    } else {
      affectedParts[selectedDiscrepantMaterialIndex] = {
        ...discrepantMaterial,
      };
    }
    if (qualityAlertLocationInfo) {
      qualityAlertLocationInfo.qualityAlertDiscrepantMaterial = affectedParts;
      setQualityAlertLocationInfo(qualityAlertLocationInfo);
    }
  };

  const affectedPartsCount = !hideCount && (
    <span>{` (${getDiscrepantMaterial().length})`}</span>
  );
  const invalid = submitted ? validate() : [];

  const onDiscrepantMaterialsModalClose = () => {
    setDiscrepantMaterialsModalOpen(false);
    setDiscrepantMaterial({} as QualityAlertDiscrepantMaterial);
    setSelectedDiscrepantMaterialIndex(null);
  };

  return (
    <div className={classnames(className, "bg-white p-4 shadow-md")}>
      <div className="flex">
        <div className="flex-1">
          <div className="mb-4 flex items-center">
            <input
              className="flex-1 rounded border border-gray-300 bg-white px-4 py-2 text-black"
              type="search"
              placeholder="Search part name, number, or description"
              value={search}
              onChange={onSearch}
            />
            <button
              className="ml-4 rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
              onClick={onAddClick}
            >
              Add New Part
            </button>
          </div>
          <PartsTableContainer
            search={search}
            onRowClick={(part: Part, rowIndex: number) =>
              onRowClickUnselectedParts(part, rowIndex)
            }
            selectedRow={unselectedParts.selectedRow}
            data={getUnselectedParts()}
          />
        </div>

        <div className="mx-4 flex items-center justify-center">
          <button
            className="text-2xl text-blue-500 hover:text-blue-600"
            onClick={onSelectPart}
          >
            &gt;
          </button>
        </div>

        <div className="flex-1 pt-9 text-black">
          <div className="mb-4 flex">
            <div
              className={classnames(
                "justify-items-center text-lg font-semibold",
                _.includes(invalid, "parts") && "text-red-500",
              )}
            >
              AFFECTED PARTS {affectedPartsCount}
            </div>
          </div>
          <DiscrepantMaterialTableContainer
            className={_.includes(invalid, "parts") ? "text-red-500" : ""}
            onRowClick={(
              dMaterial: QualityAlertDiscrepantMaterial,
              rowIndex: number,
            ) => onRowClickDiscrepantMaterial(dMaterial, rowIndex)}
            data={getDiscrepantMaterial()}
            parts={parts}
          />
        </div>
      </div>
      {discrepantMaterialsModalOpen && (
        <CustomModal
          isOpen={discrepantMaterialsModalOpen}
          onRequestClose={() => onDiscrepantMaterialsModalClose()}
          onSave={(
            quantity: number,
            responsibility: string,
            disposition: string,
          ) => saveDiscrepantMaterials(quantity, responsibility, disposition)}
          discrepantMaterial={discrepantMaterial}
          partNumber={selectedPart?.number || ""}
        />
      )}
    </div>
  );
};

export default PartsSelector;
